export type Tone = 'bad' | 'warn' | 'ok' | 'neutral';

export type RecoveryRun = {
  transactionId?: string;
  status?: string;
  startDate?: string;
  mappingDuration?: number | string;
  sendingDuration?: number | string;
  receivingDuration?: number | string;
  recordsReceived?: number | string;
  recordsSent?: number | string;
  recordsSkipped?: number | string;
  sourceQueueSize?: number | string;
  destinationQueueSize?: number | string;
  destinationDataReceivedTime?: string;
  configVersion?: string;
};

export type Finding = {
  id: string;
  tone: Exclude<Tone, 'neutral'>;
  title: string;
  detail: string;
};

export type StageStat = {
  stage: 'Source' | 'Mapping' | 'Destination';
  field: 'receivingDuration' | 'mappingDuration' | 'sendingDuration';
  median: number | null;
  p95: number | null;
  samples: number;
  slow: boolean;
};

export type FailedRun = {
  id: string;
  at: string;
  where: string;
};

export type Assessment = {
  tone: Exclude<Tone, 'neutral'>;
  verdict: string;
  stats: { label: string; value: string; tone: Tone }[];
  findings: Finding[];
  stages: StageStat[];
  failedRuns: FailedRun[];
  sampled: number;
  lastSuccessAt: string | null;
};

type Options = {
  asOf?: number;
  pollIntervalSeconds?: number | null;
};

function asNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function isFailedStatus(status: string | undefined): boolean {
  const value = status || '';
  return /fail|error/i.test(value) && !/success/i.test(value);
}

export function isSuccessStatus(status: string | undefined): boolean {
  return /success|complete|delivered|\bok\b/i.test(status || '');
}

function percentile(values: number[], p: number): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1));
  return sorted[index];
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 10_000) return `${(ms / 1000).toFixed(1)} s`;
  if (ms < 60_000) return `${Math.round(ms / 1000)} s`;
  const minutes = Math.round(ms / 60_000);
  return minutes < 120 ? `${minutes} min` : `${Math.round(minutes / 60)} h`;
}

export function formatAge(ms: number): string {
  if (ms < 60_000) return 'just now';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

export function classifyMessage(text: string): string {
  const value = text.toLowerCase();
  if (/401|403|unauthor|forbidden|token|expired|credential|not authenticated/.test(value)) return 'Authentication';
  if (/429|rate limit|too many requests/.test(value)) return 'Rate limit';
  if (/timeout|timed out|etimedout|econnreset|econnrefused|socket hang up/.test(value)) return 'Timeout or connection drop';
  if (/\b5\d\d\b|bad gateway|service unavailable|gateway timeout/.test(value)) return 'Remote server error';
  if (/mapping|required field|cannot read propert|schema|validation|cast to/.test(value)) return 'Mapping or payload';
  if (!text.trim()) return 'No error text stored';
  return 'Other failure';
}

function whereFailed(run: RecoveryRun): string {
  if (run.destinationDataReceivedTime) return 'Destination rejected the send';
  if (asNumber(run.mappingDuration) != null) return 'Failed during or after mapping';
  return 'Stopped before delivery';
}

function isPartial(run: RecoveryRun): boolean {
  if (isFailedStatus(run.status)) return false;
  if ((asNumber(run.recordsSkipped) || 0) > 0) return true;
  const received = asNumber(run.recordsReceived);
  const sent = asNumber(run.recordsSent);
  return received != null && sent != null && received > sent;
}

function stageValues(runs: RecoveryRun[], field: StageStat['field']): number[] {
  return runs
    .map((run) => asNumber(run[field]))
    .filter((value): value is number => value != null && value >= 0);
}

function buildStage(stage: StageStat['stage'], field: StageStat['field'], runs: RecoveryRun[]): StageStat {
  const values = stageValues(runs, field);
  const med = percentile(values, 0.5);
  const p95 = percentile(values, 0.95);
  const slow = values.length >= 4 && p95 != null && med != null && p95 >= 5000 && p95 >= med * 3;
  return { stage, field, median: med, p95, samples: values.length, slow };
}

function queueGrew(runs: RecoveryRun[], field: 'sourceQueueSize' | 'destinationQueueSize'): boolean {
  const points = [...runs]
    .filter((run) => run.startDate && asNumber(run[field]) != null)
    .sort((a, b) => Date.parse(a.startDate || '') - Date.parse(b.startDate || ''));
  if (points.length < 2) return false;
  const first = asNumber(points[0][field]) || 0;
  const last = asNumber(points[points.length - 1][field]) || 0;
  if (last < 10 || last <= first) return false;
  return last - first >= 20 || (first > 0 && (last - first) / first >= 0.5);
}

export function assessRecovery(runs: RecoveryRun[], options: Options = {}): Assessment {
  const asOf = options.asOf ?? Date.now();
  const sampled = runs.length;
  const failed = runs.filter((run) => isFailedStatus(run.status));
  const successes = runs.filter((run) => isSuccessStatus(run.status));
  const partials = runs.filter(isPartial);
  const stages = [
    buildStage('Source', 'receivingDuration', successes.length ? successes : runs),
    buildStage('Mapping', 'mappingDuration', successes.length ? successes : runs),
    buildStage('Destination', 'sendingDuration', successes.length ? successes : runs),
  ];
  const slow = stages.filter((stage) => stage.slow);
  const finished = failed.length + successes.length;
  const rate = finished ? failed.length / finished : 0;

  const findings: Finding[] = [];
  if (failed.length) {
    const grouped = new Map<string, number>();
    failed.forEach((run) => {
      const where = whereFailed(run);
      grouped.set(where, (grouped.get(where) || 0) + 1);
    });
    const breakdown = [...grouped.entries()].map(([where, count]) => `${count} ${where.toLowerCase()}`).join(', ');
    findings.push({
      id: 'failed',
      tone: rate >= 0.2 || successes.length === 0 ? 'bad' : 'warn',
      title: `${failed.length} failed ${failed.length === 1 ? 'run' : 'runs'} in this sample`,
      detail: breakdown,
    });
  }

  if (partials.length) {
    let skipped = 0;
    let short = 0;
    partials.forEach((run) => {
      skipped += asNumber(run.recordsSkipped) || 0;
      const received = asNumber(run.recordsReceived);
      const sent = asNumber(run.recordsSent);
      if (received != null && sent != null && received > sent) short += received - sent;
    });
    const parts = [`${partials.length} ${partials.length === 1 ? 'run' : 'runs'} finished with a gap.`];
    if (skipped) parts.push(`${skipped} records were skipped.`);
    if (short) parts.push(`${short} records were received and not sent.`);
    findings.push({
      id: 'partial',
      tone: 'warn',
      title: 'Successful runs still dropped records',
      detail: `${parts.join(' ')} A green status here does not mean the destination has every record.`,
    });
  }

  slow.forEach((stage) => {
    findings.push({
      id: `slow-${stage.stage}`,
      tone: 'warn',
      title: `${stage.stage} is the slow stage`,
      detail: `Typical ${formatDuration(stage.median || 0)}, slowest tenth ${formatDuration(stage.p95 || 0)}, across ${stage.samples} runs. Slowness here is ${stage.stage === 'Source' ? 'the system you read from' : stage.stage === 'Destination' ? 'the system you write to' : 'the mapping itself'}.`,
    });
  });

  const successTimes = successes
    .map((run) => Date.parse(run.startDate || ''))
    .filter((time) => Number.isFinite(time));
  const lastSuccess = successTimes.length ? Math.max(...successTimes) : null;
  const quietAfter = Math.max(((options.pollIntervalSeconds || 0) * 1000) * 3, 15 * 60 * 1000);
  const quiet = lastSuccess != null && asOf - lastSuccess > quietAfter && sampled > 0;
  if (quiet && lastSuccess != null) {
    findings.push({
      id: 'quiet',
      tone: 'warn',
      title: 'Nothing has succeeded recently',
      detail: `Last success was ${formatAge(asOf - lastSuccess)}. If the poll interval is shorter than that, the runner is stuck, paused, or waiting on a window that never completes.`,
    });
  }

  if (queueGrew(runs, 'sourceQueueSize') || queueGrew(runs, 'destinationQueueSize')) {
    findings.push({
      id: 'queue',
      tone: 'warn',
      title: 'A queue got deeper across this sample',
      detail: 'The backlog grew while these runs were in flight. Pause only if the destination is unhealthy. Otherwise it needs time, or the poll is faster than the writer.',
    });
  }

  const versions = [...new Set(runs.map((run) => (run.configVersion || '').trim()).filter(Boolean))];
  if (versions.length > 1) {
    findings.push({
      id: 'config',
      tone: failed.length ? 'warn' : 'ok',
      title: 'The config changed during this window',
      detail: `${versions.length} config versions show up in these runs. A bad edit is a common reason a healthy integration starts failing. Compare the current config before moving any data.`,
    });
  }

  let tone: Assessment['tone'] = 'ok';
  let verdict = 'Runs in this window are completing.';
  if (!sampled) {
    tone = 'warn';
    verdict = 'No runs in this window. Widen the range, or check that the integration is running.';
  } else if (!successes.length && !failed.length) {
    tone = 'warn';
    verdict = 'Runs in this window have not finished.';
  } else if (!successes.length && failed.length) {
    tone = 'bad';
    verdict = 'No run in this window succeeded.';
  } else if (rate >= 0.5 || (failed.length >= 5 && rate >= 0.2)) {
    tone = 'bad';
    verdict = 'Most of the finished runs failed.';
  } else if (partials.length) {
    tone = 'warn';
    verdict = 'Runs are completing, and some of them did not deliver every record.';
  } else if (slow.length) {
    tone = 'warn';
    verdict = `Runs are completing, and ${slow.map((stage) => stage.stage.toLowerCase()).join(' and ')} ${slow.length === 1 ? 'is' : 'are'} slow.`;
  } else if (quiet) {
    tone = 'warn';
    verdict = 'The last success is older than this integration usually goes.';
  } else if (findings.some((finding) => finding.tone === 'warn')) {
    tone = 'warn';
    verdict = failed.length
      ? 'Most runs are completing. A few failed in this sample.'
      : 'Runs are completing. One thing in this sample is worth a look.';
  }

  const slowest = [...stages].filter((stage) => stage.p95 != null).sort((a, b) => (b.p95 || 0) - (a.p95 || 0))[0];
  const skippedTotal = runs.reduce((sum, run) => sum + (asNumber(run.recordsSkipped) || 0), 0);

  return {
    tone,
    verdict,
    sampled,
    lastSuccessAt: lastSuccess == null ? null : new Date(lastSuccess).toISOString(),
    failedRuns: [...failed]
      .sort((a, b) => Date.parse(b.startDate || '') - Date.parse(a.startDate || ''))
      .slice(0, 8)
      .map((run) => ({
        id: run.transactionId || '',
        at: run.startDate || '',
        where: whereFailed(run),
      }))
      .filter((run) => run.id),
    stages,
    findings,
    stats: [
      { label: 'Runs', value: String(sampled), tone: 'neutral' },
      { label: 'Failed', value: String(failed.length), tone: failed.length ? 'bad' : 'ok' },
      { label: 'Skipped records', value: String(skippedTotal), tone: skippedTotal ? 'warn' : 'neutral' },
      {
        label: 'Last success',
        value: lastSuccess == null ? 'None' : formatAge(Math.max(0, asOf - lastSuccess)),
        tone: !sampled ? 'neutral' : lastSuccess == null || quiet ? 'warn' : 'ok',
      },
      {
        label: 'Slowest tail',
        value: slowest?.p95 == null ? 'Not recorded' : `${slowest.stage} ${formatDuration(slowest.p95)}`,
        tone: slow.length ? 'warn' : 'neutral',
      },
    ],
  };
}
